import { TZDate } from '@date-fns/tz';
import { format } from 'date-fns';
import DOMPurify from 'isomorphic-dompurify';
import { marked } from 'marked';
import Link from 'next/link';
import type { RssFeed } from '@/api/backoffice-schemas';
import { GameIcon } from '@/components/GameIcon';
import { SignalIcon } from '@/components/icons/SignalIcon';
import { Game } from '@/constants/game.const';
import { getGameFromRssFeedGame } from '@/utils/gameMappers';

import parse = marked.parse;

interface NewsFeedCardProps {
  data: RssFeed;
}

const NewsFeedCard = ({ data }: NewsFeedCardProps) => {
  const { link, date, title, game: rssFeedGame, description, source } = data;
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const game = getGameFromRssFeedGame(rssFeedGame);

  return (
    <Link
      className="flex flex-col gap-3 rounded-lg bg-light-gray hover:bg-light-blue-o15"
      href={link || ''}
      target="_blank"
    >
      <div className="flex flex-col gap-4 p-3">
        <div className="flex flex-row items-center justify-between">
          <p className="font-semibold text-xs leading-[14px]">
            {date ? format(new TZDate(date, userTimeZone), 'dd.MM.yyyy') : ''}
          </p>
          <div className="flex flex-row gap-3">
            <RssFeedGameIcons game={game} />
          </div>
        </div>
        <div className="flex flex-col gap-2 font-normal text-gray text-sm leading-[17px]">
          <div
            className="flex flex-col gap-2 font-semibold text-sm leading-[17px]"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(parse(title || '') as string, {
                USE_PROFILES: { html: true },
              }),
            }}
          />
          <div
            className="flex flex-col gap-2 font-normal text-sm leading-[17px]"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                parse((description || '').replace(/<img[^>]*>/g, '')) as string,
                {
                  USE_PROFILES: { html: true },
                }
              ),
            }}
          />
          <p className="font-normal text-xs leading-[14px]">
            Source: <span className="text-blue-gray">{source}</span>
          </p>
        </div>
      </div>
    </Link>
  );
};

function RssFeedGameIcons({ game }: { game: Game | null }) {
  switch (game) {
    case Game.DOTA:
      return (
        <>
          <GameIcon game={game} />
          <SignalIcon
            className="w-4 text-red"
            desc="News signal icon"
            title="Signal"
          />
        </>
      );
    case Game.CS2:
      return (
        <>
          <GameIcon game={game} />
          <SignalIcon
            className="w-4 text-orange"
            desc="News signal icon"
            title="Signal"
          />
        </>
      );
    default:
      return null;
  }
}

export default NewsFeedCard;
